<template>
  <mobile-responsive-shell :title="title">
    <div class="pur-mobile">
      <div class="pur-mobile-toolbar">
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="refresh">
          <i class="bi bi-arrow-clockwise"></i>
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          @click="$router.push({ name: 'mobile-purchases-create' })"
        >
          <i class="bi bi-plus-lg"></i> Achat
        </button>
        <button
          type="button"
          class="btn btn-outline-primary btn-sm"
          @click="$router.push({ name: 'mobile-purchases-items-create' })"
        >
          <i class="bi bi-box-seam"></i> Article
        </button>
        <button
          type="button"
          class="btn btn-outline-primary btn-sm"
          :class="{ active: showFilters }"
          @click="showFilters = !showFilters"
        >
          <i class="bi bi-funnel"></i> Filtres
        </button>
      </div>

      <div v-show="showFilters" class="pur-mobile-filters card card-body py-3 mb-3">
        <div class="mb-2">
          <label class="form-label small mb-0">Fournisseur</label>
          <select v-model="filter.supplier_reference" class="form-select form-select-sm">
            <option value="">—</option>
            <option
              v-for="s in suppliers"
              :key="s.reference"
              :value="s.reference"
            >
              {{ s.society }}
            </option>
          </select>
        </div>
        <div class="mb-2">
          <label class="form-label small mb-0">Produit</label>
          <select v-model="filter.item_reference" class="form-select form-select-sm">
            <option value="">—</option>
            <option
              v-for="it in items"
              :key="it.reference"
              :value="it.reference"
            >
              {{ it.fullName }}
            </option>
          </select>
        </div>
        <div class="row g-2 mb-2">
          <div class="col-6">
            <label class="form-label small mb-0">Du</label>
            <input v-model="filter.startDate" type="date" class="form-control form-control-sm" />
          </div>
          <div class="col-6">
            <label class="form-label small mb-0">Au</label>
            <input v-model="filter.endDate" type="date" class="form-control form-control-sm" />
          </div>
        </div>
        <div class="mb-2">
          <label class="form-label small mb-0">État paiement</label>
          <select v-model="filter.paymentStatus" class="form-select form-select-sm">
            <option value="">—</option>
            <option value="Payé">Payé</option>
            <option value="Impayé">Impayé</option>
          </select>
        </div>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-primary btn-sm flex-fill" @click="applyFilter">
            Appliquer
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm flex-fill" @click="resetFilter">
            Réinit.
          </button>
        </div>
      </div>

      <p v-if="!purchases || !purchases.length" class="text-muted text-center py-4">
        Aucun achat.
      </p>

      <div v-else class="pur-mobile-list">
        <article
          v-for="(row, idx) in purchases"
          :key="row.reference || idx"
          class="pur-mobile-card"
        >
          <header class="pur-mobile-card-head">
            <span class="pur-mobile-ref">{{ row.reference }}</span>
            <button
              type="button"
              class="btn btn-sm btn-outline-primary"
              @click="goDetails(row)"
            >
              <i class="bi bi-eye"></i>
            </button>
          </header>
          <div class="pur-mobile-meta">
            {{ supplierLabel(row) }} · {{ row.date || "—" }}
          </div>
          <div class="pur-mobile-stats">
            <div>
              <span class="pur-lbl">M. total</span>
              <span class="text-primary fw-semibold">{{ fmt(row.totalPriceTTC) }}</span>
            </div>
            <div>
              <span class="pur-lbl">Qté</span>
              <span class="text-secondary">{{ fmt(row.totalQuantity) }}</span>
            </div>
            <div>
              <span class="pur-lbl">Payé</span>
              <span class="text-success">{{ fmt(row.amountPaid) }}</span>
            </div>
            <div>
              <span class="pur-lbl">Impayé</span>
              <span class="text-danger">{{ fmt(row.amountUnpaid) }}</span>
            </div>
          </div>
          <footer class="pur-mobile-foot text-muted small">
            <span v-if="row.userFullName">{{ row.userFullName }}</span>
          </footer>
        </article>
      </div>

      <div v-if="purchases && purchases.length" class="pur-mobile-summary">
        <div>Qté Σ <strong>{{ fmt(totalQuantity) }}</strong></div>
        <div>TTC Σ <strong>{{ fmt(totalPriceTTC) }}</strong></div>
        <div>Payé Σ <strong>{{ fmt(totalPaid) }}</strong></div>
        <div>Impayé Σ <strong>{{ fmt(totalUnpaid) }}</strong></div>
      </div>
    </div>
  </mobile-responsive-shell>
</template>

<script>
import { mapGetters } from "vuex";
import MobileResponsiveShell from "../components/MobileResponsiveShell.vue";

const defaultFilter = () => ({
  supplier_reference: "",
  item_reference: "",
  startDate: new Date(new Date().setFullYear(new Date().getFullYear()))
    .toLocaleString("en-CA")
    .split(",")[0],
  endDate: new Date(new Date().setFullYear(new Date().getFullYear()))
    .toLocaleString("en-CA")
    .split(",")[0],
  paymentStatus: "",
});

export default {
  name: "MobilePurchasesListing",
  components: { MobileResponsiveShell },
  data() {
    return {
      title: "Achats",
      showFilters: false,
      filter: defaultFilter(),
    };
  },
  computed: {
    ...mapGetters("purchase", {
      purchases: "getAll",
      totalQuantity: "getPurchasesTotalQuantity",
      totalPriceTTC: "getPurchasesTotalPriceTTC",
      totalPaid: "getPurchasesTotalPaid",
      totalUnpaid: "getPurchasesTotalUnpaid",
    }),
    ...mapGetters("supplier", { suppliers: "getAll" }),
    ...mapGetters("item", { items: "getAll" }),
  },
  mounted() {
    this.$store.dispatch("purchase/getAll");
    this.$store.dispatch("supplier/getAll");
    this.$store.dispatch("item/getAll");
  },
  methods: {
    fmt(v) {
      if (v === undefined || v === null || v === "") return "—";
      const n = parseFloat(v);
      if (Number.isNaN(n)) return String(v);
      return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    supplierLabel(row) {
      if (row.supplierSociety) return row.supplierSociety;
      if (row.supplier && row.supplier.society) return row.supplier.society;
      return "—";
    },
    async refresh() {
      await this.$store.dispatch("purchase/getAll");
    },
    applyFilter() {
      this.$store.dispatch("purchase/filter", { ...this.filter });
    },
    resetFilter() {
      this.filter = defaultFilter();
      this.$store.dispatch("purchase/filter", { ...this.filter });
    },
    goDetails(row) {
      this.$router.push({
        name: "mobile-purchases-details",
        params: { reference: row.reference },
      });
    },
  },
};
</script>

<style scoped>
.pur-mobile-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.pur-mobile-filters {
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.pur-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.pur-mobile-card {
  background: #fff;
  border-radius: 12px;
  padding: 0.875rem 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.pur-mobile-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}
.pur-mobile-ref {
  font-weight: 700;
  font-size: 1.1rem;
  word-break: break-all;
}
.pur-mobile-meta {
  font-size: 1rem;
  color: #495057;
  margin-bottom: 0.5rem;
}
.pur-mobile-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.35rem 0.75rem;
  font-size: 1rem;
}
.pur-lbl {
  display: block;
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #6c757d;
}
.pur-mobile-foot {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #eee;
}
.pur-mobile-summary {
  margin-top: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 10px;
  font-size: 0.95rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.35rem;
}
</style>
